import { formatDisplayTime } from "@/utils/date";
import { ExpandMore, NavigateBefore, NavigateNext } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Breadcrumbs,
  Button,
  Divider,
  Grid,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React, { ReactNode, useState } from "react";
import { HowToViewGraphsDialog } from "./commons/HowToViewGraphsDialog";
import SleepDiaryChart, {
  LegendItem,
  PlotType,
} from "./commons/SleepDiaryChart";
import {
  MeasurementDetailsLifestyleAndHealthConditionsDetail,
  MeasurementDetailsSleepData,
  UserType,
} from "./types";

/**
 * 測定データの詳細データ
 */
export interface MeasurementDetailsResponse2 {
  /**
   * レポートのバージョン
   */
  publish_version: string;
  /**
   * 測定データ
   */
  measured_data: {
    /** 測定日 */
    date: string;
    /** 就寝時間  */
    bedtime: string | null;
    /** 起床時間 */
    arise_time: string | null;
    /** グラフデータ */
    chart_data:
      | {
          /** 開始時間 */
          from: string;
          /** 終了時間 */
          to: string;
          /**
           * 描画するグラフの種類
           */
          type: PlotType;
        }[]
      | null;
    /** 睡眠データ */
    sleep_data: MeasurementDetailsSleepData;
    /** 生活習慣と健康状態 */
    lifestyle_and_health_conditions: Record<
      LifestyleAndHealthConditionTypeForMeasurementDetailsType,
      MeasurementDetailsLifestyleAndHealthConditionsDetail
    >;
  }[];
}

/**
 * 分をh時間m分の形式にします。
 * 値 が null の場合 "-" を返却します。
 * @param minutes
 * @returns
 */
function formatTime(minutes: number | null) {
  if (minutes == null) {
    return "-";
  }
  return formatDisplayTime(minutes);
}

type TabType = "sleep_data" | "lifestyle_and_health_conditions";

/**
 * 測定データの詳細プロパティ
 */
export type MeasurementDetailsProps = {
  /** レポートUUID */
  uuid: string | undefined;

  /**
   * ユーザー種別
   * @see {@link UserType}
   */
  userType: UserType;

  /**
   * 測定データの詳細データ
   *
   * @see {@link MeasurementDetailsResponse2}
   */
  data: MeasurementDetailsResponse2;
};
/**
 * 測定データの詳細
 *
 * @param param0 {@link MeasurementDetailsProps}
 * @returns
 */
export default function MeasurementDetails2({
  uuid,
  userType,
  data,
}: MeasurementDetailsProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [tab, setTab] = useState<TabType>("sleep_data");
  const [howToViewGraphs, setHowToViewGraphs] = useState(false);
  return (
    <Stack sx={{ px: 6 }}>
      <HowToViewGraphsDialog
        open={howToViewGraphs}
        setOpen={setHowToViewGraphs}
      />
      <Stack sx={{ pt: 12, pb: 8, gap: 2 }}>
        <Breadcrumbs maxItems={2} separator={<NavigateNext fontSize="small" />}>
          <Link
            href={`/${userType}/report/${uuid}`}
            color="text.secondary"
            style={{ textDecoration: "none" }}
          >
            <Typography variant="body2">レポートTOP</Typography>
          </Link>
          <Typography variant="body2">測定データの詳細</Typography>
        </Breadcrumbs>
        <Typography
          variant="h5"
          sx={{ textAlign: "justify", color: "primary.main" }}
        >
          測定データの詳細
        </Typography>
      </Stack>
      <Divider />
      <Stack sx={{ p: "24px 16px 24px 16px" }}>
        <Stack direction="row" justifyContent="end" sx={{ gap: 3, mb: 4 }}>
          <LegendItem
            imageName="image_report_how_to_view_graphs_light_off.svg"
            text="消灯"
          />
          <LegendItem
            imageName="image_report_how_to_view_graphs_wake_up.svg"
            text="起床"
          />
          <LegendItem
            imageName="image_report_how_to_view_graphs_sleep.svg"
            text="睡眠"
          />
          <LegendItem
            imageName="image_report_how_to_view_graphs_mid-awakening.svg"
            text="睡眠中の覚醒"
          />
        </Stack>
        <Stack sx={{ height: "304px" }}>
          <SleepDiaryChart
            activeIndex={activeIndex}
            onChartClick={(index) => {
              setActiveIndex(index);
            }}
            data={data.measured_data.map((data) => ({
              date: data.date,
              bedtime: data.bedtime == null ? null : new Date(data.bedtime),
              ariseTime:
                data.arise_time == null ? null : new Date(data.arise_time),
              sleepData: (data.chart_data || [])
                .filter((d) => d.type !== "nap")
                .map((d) => ({
                  from: new Date(d.from),
                  to: new Date(d.to),
                  type: d.type,
                })),
            }))}
          />
        </Stack>
      </Stack>
      <Divider />
      <Stack
        sx={{
          p: "24px 16px 24px 16px",
          justifyContent: "space-between",
          alignItems: "center",
        }}
        direction="row"
      >
        <IconButton
          onClick={() => setActiveIndex(activeIndex - 1)}
          disabled={activeIndex <= 0}
        >
          <NavigateBefore />
        </IconButton>
        <Typography variant="subtitle1">{activeIndex + 1}日目</Typography>
        <IconButton
          onClick={() => setActiveIndex(activeIndex + 1)}
          disabled={data.measured_data.length - 1 <= activeIndex}
        >
          <NavigateNext />
        </IconButton>
      </Stack>
      <Stack
        sx={{
          p: "0",
        }}
      >
        <Grid
          container
          spacing={2}
          sx={{
            backgroundColor: "#f6f5f4",
            color: "text.secondary",
            borderRadius: 32,
            mb: "40px",
            "& .MuiGrid-item": { padding: 0 },
          }}
        >
          <Grid size={6}>
            <TabButton tabType="sleep_data" currentTab={tab} setTab={setTab}>
              睡眠データ
            </TabButton>
          </Grid>
          <Grid size={6}>
            <TabButton
              tabType="lifestyle_and_health_conditions"
              currentTab={tab}
              setTab={setTab}
            >
              生活習慣と健康状態
            </TabButton>
          </Grid>
        </Grid>
      </Stack>
      <Stack>
        {data.measured_data.map((d, i) => (
          <TabData key={i} data={d} tab={tab} isDisplay={i === activeIndex} />
        ))}
      </Stack>
    </Stack>
  );
}

/**
 * 睡眠データのプロパティ
 */
type SleepDataProps = {
  /**
   * 睡眠データのデータ {@link MeasurementDetailsSleepData}
   */
  data: MeasurementDetailsSleepData;
};
/**
 * 睡眠のデータ
 * @param props {@link SleepDataProps}
 * @returns
 */
function SleepData(props: SleepDataProps) {
  return (
    <Stack>
      <Stack>
        <Typography
          variant="subtitle2"
          sx={{ color: "text.secondary", paddingBottom: "8px" }}
        >
          睡眠に関する記録
        </Typography>
        <TableContainer
          sx={{
            borderTopWidth: "1px",
            borderTopColor: "rgba(0, 0, 0, 0.12)",
            borderTopStyle: "solid",
            borderRadius: "0px",
            "& td, th": {
              p: 2,
            },
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ maxWidth: "50%" }}>
                  <Typography variant="caption">項目</Typography>
                </TableCell>
                <TableCell
                  sx={{
                    maxWidth: "50%",
                    textAlign: "right",
                    whiteSpace: "nowrap",
                  }}
                >
                  <Typography variant="caption">あなたの結果</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell sx={{ maxWidth: "50%" }}>
                  <Typography variant="body2" align="justify">
                    睡眠時間
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    maxWidth: "50%",
                    textAlign: "right",
                  }}
                >
                  <Typography variant="body2" align="right">
                    {formatTime(props.data.sleep_records.sleeping_time)}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ maxWidth: "50%" }}>
                  <Typography variant="body2" align="justify">
                    睡眠効率
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    maxWidth: "50%",
                    textAlign: "right",
                  }}
                >
                  <Typography variant="body2" align="right">
                    {props.data.sleep_records.sleep_efficiency == null
                      ? "-"
                      : `${props.data.sleep_records.sleep_efficiency}%`}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ maxWidth: "50%" }}>
                  <Typography variant="body2" align="justify">
                    消灯時刻
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    whiteSpace: "nowrap",
                    textAlign: "right",
                  }}
                >
                  <Typography variant="body2" align="right">
                    {props.data.sleep_records.in_bed ?? "-"}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ maxWidth: "50%" }}>
                  <Typography variant="body2" align="justify">
                    入眠潜時
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    maxWidth: "50%",
                    textAlign: "right",
                  }}
                >
                  <Typography variant="body2" align="right">
                    {formatTime(props.data.sleep_records.to_fall_asleep)}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ maxWidth: "50%" }}>
                  <Typography variant="body2" align="justify">
                    入眠時刻
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    maxWidth: "50%",
                    textAlign: "right",
                  }}
                >
                  <Typography variant="body2" align="right">
                    {props.data.sleep_records.fell_asleep ?? "-"}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ maxWidth: "50%" }}>
                  <Typography variant="body2" align="justify">
                    中途覚醒時間
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    maxWidth: "50%",
                    textAlign: "right",
                  }}
                >
                  <Typography variant="body2" align="right">
                    {formatTime(props.data.sleep_records.awakening_time)}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ maxWidth: "50%" }}>
                  <Typography variant="body2" align="justify">
                    覚醒時刻
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    maxWidth: "50%",
                    textAlign: "right",
                  }}
                >
                  <Typography variant="body2" align="right">
                    {props.data.sleep_records.awake ?? "-"}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ maxWidth: "50%" }}>
                  <Typography variant="body2" align="justify">
                    起床時刻（寝床から出た時刻）
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    maxWidth: "50%",
                    textAlign: "right",
                  }}
                >
                  <Typography variant="body2" align="right">
                    {props.data.sleep_records.arise_time ?? "-"}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
      <Stack sx={{ paddingTop: "40px" }}>
        <Typography
          variant="subtitle2"
          sx={{ color: "text.secondary", paddingBottom: "8px" }}
        >
          あなた自身による記録
        </Typography>
        <TableContainer
          sx={{
            borderTopWidth: "1px",
            borderTopColor: "rgba(0, 0, 0, 0.12)",
            borderTopStyle: "solid",
            borderRadius: "0px",
            "& td, th": {
              p: 2,
            },
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ maxWidth: "50%" }}>
                  <Typography variant="caption">項目</Typography>
                </TableCell>
                <TableCell
                  sx={{
                    maxWidth: "50%",
                    textAlign: "right",
                    whiteSpace: "nowrap",
                  }}
                >
                  <Typography variant="caption">あなたの結果</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell sx={{ maxWidth: "50%" }}>
                  <Typography variant="body2" align="justify">
                    すっきり起きられたか
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    maxWidth: "50%",
                    textAlign: "right",
                  }}
                >
                  <Typography variant="body2" align="right">
                    {props.data.your_own_records.ease_of_wake_up}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ maxWidth: "50%" }}>
                  <Typography variant="body2" align="justify">
                    目覚めた時の気分
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    maxWidth: "50%",
                    textAlign: "right",
                  }}
                >
                  <Typography variant="body2" align="right">
                    {props.data.your_own_records.wake_up_feeing}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ maxWidth: "50%" }}>
                  <Typography variant="body2" align="justify">
                    目覚めた時の疲れの取れ具合
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    maxWidth: "50%",
                    textAlign: "right",
                  }}
                >
                  <Typography variant="body2" align="right">
                    {props.data.your_own_records.fatigue_recovery}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ maxWidth: "50%" }}>
                  <Typography variant="body2" align="justify">
                    今日の睡眠に満足したか
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    maxWidth: "50%",
                    textAlign: "right",
                  }}
                >
                  <Typography variant="body2" align="right">
                    {props.data.your_own_records.satisfied_sleep_today}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ maxWidth: "50%" }}>
                  <Typography variant="body2" align="justify">
                    日中の眠気
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    maxWidth: "50%",
                    textAlign: "right",
                  }}
                >
                  <Typography variant="body2" align="right">
                    {props.data.your_own_records.daytime_sleepiness}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ maxWidth: "50%" }}>
                  <Typography variant="body2" align="justify">
                    日中の疲労感
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    maxWidth: "50%",
                    textAlign: "right",
                  }}
                >
                  <Typography variant="body2" align="right">
                    {props.data.your_own_records.daytime_fatigue}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ maxWidth: "50%" }}>
                  <Typography variant="body2" align="justify">
                    仕事の出来
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    maxWidth: "50%",
                    textAlign: "right",
                  }}
                >
                  <Typography variant="body2" align="right">
                    {props.data.your_own_records.work_performance}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    </Stack>
  );
}

const LIFESTYLE_AND_HEALTH_CONDITION_ORDER = [
  "body_condition",
  "before_sleep_mind",
  "light",
  "bluelight",
  "exercise",
  "bath",
  "nap",
  "nightmeal",
  "caffeine",
  "alcohol",
  "morning_light",
] as const;
type LifestyleAndHealthConditionTypeForMeasurementDetailsType =
  (typeof LIFESTYLE_AND_HEALTH_CONDITION_ORDER)[number];
/**
 * 生活習慣と健康状態のタイトル
 */
const TITLES: Readonly<
  Record<LifestyleAndHealthConditionTypeForMeasurementDetailsType, string>
> = {
  body_condition: "体の調子",
  before_sleep_mind: "寝る前の気分",
  light: "部屋の明かり",
  bluelight: "ブルーライト",
  exercise: "運動",
  bath: "入浴",
  nap: "仮眠",
  nightmeal: "夜食",
  caffeine: "カフェイン",
  alcohol: "アルコール",
  morning_light: "朝の光",
};

/**
 * 生活習慣と健康状態のアコーディオンのプロパティ
 */
type LifestyleAndHealthConditionAccordionProps = {
  /**
   * アコーディオンのタイトル
   */
  title: string;
  /**
   * アコーディオンの内容
   * @see {@link MeasurementDetailsLifestyleAndHealthConditionsDetail}
   */
  content: MeasurementDetailsLifestyleAndHealthConditionsDetail;
  /**
   * {@link LifestyleAndHealthConditionType}
   */
  type: LifestyleAndHealthConditionTypeForMeasurementDetailsType;
};
function LifestyleAndHealthConditionAccordion(
  props: LifestyleAndHealthConditionAccordionProps
) {
  const [open, setOpen] = useState(false);
  return (
    <Stack
      sx={{
        borderRadius: "8px",
        boxShadow: "0px 0px 0px 1px #E0E0E0",
        overflow: "hidden",
      }}
    >
      <Accordion
        expanded={open}
        onChange={() => {
          setOpen((open) => !open);
        }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Stack direction="row" sx={{ gap: 2, alignItems: "center" }}>
            <Typography>{props.title}</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          {props.content?.data.map((d, i) => (
            <Stack key={i}>
              <Stack
                sx={{ p: "12px 16px", backgroundColor: "background.default" }}
              >
                <Typography variant="body2">
                  Q{(props.content.data.length ?? 0) > 1 ? i + 1 : ""}.{" "}
                  {d.question}
                </Typography>
              </Stack>
              <Stack sx={{ p: "12px 16px", textAlign: "right" }}>
                {d.answers.map((a, i) => (
                  <React.Fragment key={i}>
                    <Typography variant="body2" key={i}>
                      {a}
                    </Typography>
                  </React.Fragment>
                ))}
              </Stack>
            </Stack>
          ))}
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
}

/**
 * 生活習慣と健康状態のプロパティ
 */
type LifestyleAndHealthConditionProps = {
  /**
   * 生活習慣と健康状態のデータ
   */
  data: Record<
    LifestyleAndHealthConditionTypeForMeasurementDetailsType,
    MeasurementDetailsLifestyleAndHealthConditionsDetail
  >;

  /**
   * 画面に表示させるかどうか(表示させる場合、true)
   */
  isDisplay: boolean;
};

/**
 * 生活習慣と健康状態

 * @param props {@link LifestyleAndHealthConditionProps}
 * @returns
 */
function LifestyleAndHealthCondition(props: LifestyleAndHealthConditionProps) {
  return (
    <Stack sx={{ gap: 4, display: `${props.isDisplay ? "" : "none"}` }}>
      {LIFESTYLE_AND_HEALTH_CONDITION_ORDER.map((k, i) => (
        <LifestyleAndHealthConditionAccordion
          key={i}
          type={k}
          title={TITLES[k]}
          content={props.data[k]}
        />
      ))}
    </Stack>
  );
}

/**
 * 睡眠データ／生活習慣と健康状態を切り替えるボタンのプロパティ
 */
type TabButtonProps = {
  /** 子ノード */
  children: ReactNode;
  /**
   * このボタンの{@link TabType}
   *
   * @see TabType
   */
  tabType: TabType;
  /**
   * 現在表示している{@link TabType}
   * @see TabType
   */
  currentTab: TabType;
  /**
   * ボタンが押下された場合の処理
   *
   * @param tab
   * @returns
   */
  setTab: (tab: TabType) => void;
};
/**
 * 睡眠データ／生活習慣と健康状態を切り替えるボタン
 *
 * @param props {@link TabButtonProps}
 * @returns
 */
function TabButton(props: TabButtonProps) {
  const isActive = props.currentTab === props.tabType;
  return (
    <Button
      sx={{
        borderRadius: 32,
        width: "100%",
        fontSize: "14px",
        px: 0,
        boxShadow: "none",
        "&:hover": {
          boxShadow: "none",
        },
        "&:active": {
          boxShadow: "none",
        },
      }}
      disableRipple
      variant={isActive ? "contained" : "text"}
      onClick={() => props.setTab(props.tabType)}
    >
      <Typography
        variant="subtitle2"
        sx={{ color: `${isActive ? "#FFFFFF" : "primary.main"}` }}
      >
        {props.children}
      </Typography>
    </Button>
  );
}

type TabDataProps = {
  data: {
    /** 睡眠データ */
    sleep_data: MeasurementDetailsSleepData;
    /** 生活習慣と健康状態 */
    lifestyle_and_health_conditions: Record<
      LifestyleAndHealthConditionTypeForMeasurementDetailsType,
      MeasurementDetailsLifestyleAndHealthConditionsDetail
    >;
  };
  /** 表示タブ */
  tab: TabType;
  /** ディスプレイに表示するかどうか */
  isDisplay: boolean;
};
/**
 * 睡眠データ／生活習慣と健康状態の内容
 *
 * @param props {@link TabDataProps}
 * @returns
 */
function TabData(props: TabDataProps) {
  return (
    <Stack
      sx={{
        pb: 20,
        display: `${props.isDisplay ? "" : "none"}`,
      }}
    >
      {props.tab === "sleep_data" && <SleepData data={props.data.sleep_data} />}
      <LifestyleAndHealthCondition
        data={props.data.lifestyle_and_health_conditions}
        isDisplay={props.tab === "lifestyle_and_health_conditions"}
      />
    </Stack>
  );
}
